import { FILTER_NODE_KEY_AND_FACTORY } from 'elements/hardwareFilter/constantsV2'
import { FilterValuesType } from 'elements/hardwareFilter/typeV2'
import { useEffect, useMemo } from 'react'
import { ComponentTypesV2 } from 'types/selectComponent'

export type FilterConfig = {
  key: string
  rendererComponentWithAccordion: any
}

type Props = {
  filtersConfigs: FilterConfig[]
  allFilterValues: FilterValuesType
  componentTypes?: ComponentTypesV2[]
  setModifiableFilterValues: (
    filterValues: FilterValuesType,
    displayedFilters?: { [key: string]: boolean },
    immediate?: boolean
  ) => void
  onFiltersReady?: () => void
}

export const useHardwareSelectorFilterNodes = ({
  filtersConfigs,
  allFilterValues,
  componentTypes,
  setModifiableFilterValues,
  onFiltersReady,
}: Props) => {
  const filterNodes = useMemo(() => {
    return filtersConfigs.map(({ key, rendererComponentWithAccordion, ...rest }) => {
      const factory = FILTER_NODE_KEY_AND_FACTORY[key]
      return factory({ rendererComponent: rendererComponentWithAccordion, key, ...rest })
    })
  }, [filtersConfigs])

  // Allow filterNodes to make initial mods to the selected filters.
  // Exclude page/perPage - they are query params, not filter params. Including them would
  // cause FilterPresenter to reset page to 1 and can override pagination in API requests.
  const PAGINATION_KEYS = ['page', 'perPage']
  useEffect(() => {
    const { page, perPage, ...filterValuesOnly } = allFilterValues
    let filtersNow = { ...filterValuesOnly }
    let filtersChanged = false
    let types = new Set(componentTypes) || new Set()
    filterNodes.forEach((node) => {
      if (node.getDefaultFilterValues) {
        const values = node.getDefaultFilterValues({
          filterValues: allFilterValues,
          componentTypes: types,
        })
        if (!values) return

        Object.keys(values).forEach((key) => {
          if (PAGINATION_KEYS.includes(key)) return
          if (filtersNow[key] !== values[key]) {
            filtersChanged = true
            filtersNow[key] = values[key]
          }
        })
      }
    })
    if (filtersChanged) {
      setModifiableFilterValues(filtersNow, undefined, true)
    }
    onFiltersReady?.()
  }, [filterNodes])

  return { filterNodes }
}
